import React, { useState, useEffect } from 'react';
import {
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  Select,
  Card,
  InputNumber,
  DatePicker,
  Switch,
  Divider,
  Row,
  Col,
  Typography,
  Table,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  PrinterOutlined,
  DollarOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchServiceOrder,
  createServiceOrder,
  updateServiceOrder,
  deleteServiceOrderLine,
  getServiceOrderStatuses,
  fetchServiceItems,
  fetchExternalServiceItems,
  createServiceItem,
  createExternalServiceItem,
  fetchComplaintItems,
  fetchCustomerVehicle,
  searchCustomerVehicles,
  createCustomerVehicle,
  VEHICLE_ASSIGNED_TO_OTHER_CODE,
  createServiceOrderLine,
  updateServiceOrderLine,
  TAX_RATE_OPTIONS,
  type ServiceOrderDto,
  type ServiceOrderLineDto,
  type ServiceItemDto,
  type ExternalServiceItemDto,
  type ComplaintItemDto,
  type CustomerVehicleDto,
} from '../services/serviceOrderService';
import { api } from '../services/api';
import { fetchPersonnelList, type PersonnelListItem } from '../services/personnelService';
import { fetchBrands, fetchModels, type BrandDto, type ModelDto } from '../services/vehicleService';
import AccountSelectModal from '../components/AccountSelectModal';
import ProductSelectModal from '../components/ProductSelectModal';
import DamageAssessmentSection from '../components/DamageAssessmentSection';
import { trUpperNormalize } from '../utils/trLocale';

const { TextArea } = Input;
const { Title } = Typography;

interface Account {
  id: number;
  accountCode: string;
  accountName: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  phone?: string;
}

const MOBILE_BREAKPOINT = 768;

const ServiceOrderFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const roleCodes = user?.roleCodes ?? [];
  const isAdmin = roleCodes.includes('ADMIN');
  const orderId = id ? parseInt(id, 10) : null;
  const customerIdParam = searchParams.get('customerId');
  const customerId = customerIdParam ? parseInt(customerIdParam, 10) : null;
  const vehicleIdParam = searchParams.get('vehicleId');
  const vehicleIdFromUrl = vehicleIdParam ? parseInt(vehicleIdParam, 10) : null;

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [loading, setLoading] = useState(false);
  const [editingOrder, setEditingOrder] = useState<ServiceOrderDto | null>(null);
  const [form] = Form.useForm();
  const useOrderPeriodicMaintenance = Form.useWatch('useOrderPeriodicMaintenance', form);
  const watchedStatus = Form.useWatch('status', form);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [serviceItems, setServiceItems] = useState<ServiceItemDto[]>([]);
  const [externalServiceItems, setExternalServiceItems] = useState<ExternalServiceItemDto[]>([]);
  const [complaintItems, setComplaintItems] = useState<ComplaintItemDto[]>([]);
  const [users, setUsers] = useState<PersonnelListItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Account | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<CustomerVehicleDto | null>(null);
  const [vehicles, setVehicles] = useState<CustomerVehicleDto[]>([]);
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [externalAccountModalVisible, setExternalAccountModalVisible] = useState(false);
  const [externalAccountLineIndex, setExternalAccountLineIndex] = useState<number | null>(null);
  const [vehicleModalVisible, setVehicleModalVisible] = useState(false);
  const [addVehicleModalVisible, setAddVehicleModalVisible] = useState(false);
  const [addVehicleForm] = Form.useForm();
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [models, setModels] = useState<ModelDto[]>([]);
  const [addVehicleBrandId, setAddVehicleBrandId] = useState<number | undefined>();
  const [addVehicleSaving, setAddVehicleSaving] = useState(false);
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [lines, setLines] = useState<Array<Partial<ServiceOrderLineDto> & { tempId?: string }>>([]);
  const [addServiceItemModalVisible, setAddServiceItemModalVisible] = useState(false);
  const [addExternalServiceItemModalVisible, setAddExternalServiceItemModalVisible] = useState(false);
  const [addServiceItemLineIndex, setAddServiceItemLineIndex] = useState<number | null>(null);
  const [addExternalServiceItemLineIndex, setAddExternalServiceItemLineIndex] = useState<number | null>(null);
  const [addServiceItemForm] = Form.useForm();
  const [addExternalServiceItemForm] = Form.useForm();
  const [addServiceItemSaving, setAddServiceItemSaving] = useState(false);
  const [addExternalServiceItemSaving, setAddExternalServiceItemSaving] = useState(false);
  /** Sadece düzenleme: durum SMS (kabul/teslim vb.) bu fiş için açık mı */
  const [sendStatusSms, setSendStatusSms] = useState(true);

  useEffect(() => {
    loadStatuses();
    loadServiceItems();
    loadExternalServiceItems();
    loadComplaintItems();
    loadPersonnel();
    loadBrandsForAddVehicle();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (addVehicleBrandId) {
      fetchModels(addVehicleBrandId).then(setModels).catch(() => setModels([]));
    } else {
      setModels([]);
      addVehicleForm.setFieldsValue({ modelId: undefined });
    }
  }, [addVehicleBrandId]);

  useEffect(() => {
    if (orderId) {
      loadOrder(orderId);
      return;
    }

    initNewOrder();
    if (customerId && !isNaN(customerId)) {
      preloadCustomer(customerId, vehicleIdFromUrl && !isNaN(vehicleIdFromUrl) ? vehicleIdFromUrl : null);
    }
  }, [orderId, customerId, vehicleIdFromUrl]);

  const initNewOrder = () => {
    setEditingOrder(null);
    setSelectedCustomer(null);
    setSelectedVehicle(null);
    setVehicles([]);
    setLines([]);
    form.resetFields();
    setSendStatusSms(true);
    form.setFieldsValue({
      orderDate: dayjs(),
      status: 1,
      useOrderPeriodicMaintenance: false,
      orderPeriodicMaintenanceMonths: 0,
    });
  };

  const preloadCustomer = async (idValue: number, preloadVehicleId: number | null = null) => {
    try {
      const { data } = await api.get(`/accounts/${idValue}`);
      const accountName = data?.accountName || data?.name || data?.title || '';
      if (!accountName) return;
      const account: Account = {
        id: data.id ?? idValue,
        accountCode: data.accountCode ?? '',
        accountName,
        phone: data.phone,
      };
      setSelectedCustomer(account);
      form.setFieldsValue({ customerId: account.id });
      const vehicleList = await searchCustomerVehicles({ customerName: account.accountName });
      setVehicles(vehicleList);

      if (preloadVehicleId) {
        const vehicle = vehicleList.find((v) => v.id === preloadVehicleId) ?? await fetchCustomerVehicle(preloadVehicleId).catch(() => null);
        if (vehicle && vehicle.customerId === idValue) {
          setSelectedVehicle(vehicle);
          form.setFieldsValue({ vehicleId: vehicle.id, entryKm: vehicle.currentKm ?? undefined });
        }
      }
    } catch (error) {
      message.error('Müşteri bilgileri yüklenirken hata oluştu');
    }
  };

  const loadOrder = async (idValue: number) => {
    try {
      setLoading(true);
      const fullOrder = await fetchServiceOrder(idValue);
      if (fullOrder.status === 5 || fullOrder.status === 6) {
        message.warning('Teslim edilmiş veya iptal edilmiş servis fişleri düzenlenemez.');
        navigate('/service-orders');
        return;
      }
      setEditingOrder(fullOrder);
      const [accountRes, vehicleRes] = await Promise.allSettled([
        api.get(`/accounts/${fullOrder.customerId}`),
        fetchCustomerVehicle(fullOrder.vehicleId),
      ]);

      const accountData = accountRes.status === 'fulfilled' ? accountRes.value.data : null;
      const customerAccount: Account = {
        id: fullOrder.customerId,
        accountName: accountData?.accountName ?? fullOrder.customerName,
        accountCode: accountData?.accountCode ?? '',
        firstName: accountData?.firstName ?? '',
        lastName: accountData?.lastName ?? '',
        companyName: accountData?.companyName ?? undefined,
        phone: accountData?.phone ?? '',
      };
      setSelectedCustomer(customerAccount);

      if (vehicleRes.status === 'fulfilled') {
        setSelectedVehicle(vehicleRes.value);
      } else {
        setSelectedVehicle({
          id: fullOrder.vehicleId,
          plate: fullOrder.vehiclePlate,
          customerId: fullOrder.customerId,
          customerName: fullOrder.customerName,
          currentKm: fullOrder.entryKm || 0,
          vehicleInfo: fullOrder.vehicleInfo ?? undefined,
          isActive: true,
        } as CustomerVehicleDto);
      }
      setLines((fullOrder.lines ?? []).map((l) => ({ ...l, tempId: `line-${l.id}` })));
      setSendStatusSms(fullOrder.sendStatusSms !== false);
      form.setFieldsValue({
        customerId: fullOrder.customerId,
        vehicleId: fullOrder.vehicleId,
        responsibleUserId: fullOrder.responsibleUserId,
        deliveredByUserId: fullOrder.deliveredByUserId,
        entryKm: fullOrder.entryKm,
        exitKm: fullOrder.exitKm,
        orderDate: dayjs(fullOrder.orderDate),
        status: fullOrder.status,
        notes: fullOrder.notes,
        complaintNotes: fullOrder.complaintNotes,
        complaintItemIds: (fullOrder.complaintItems ?? []).map((item) => item.id),
        useOrderPeriodicMaintenance: fullOrder.useOrderPeriodicMaintenance ?? false,
        orderPeriodicMaintenanceMonths: fullOrder.orderPeriodicMaintenanceMonths ?? 0,
      });
      const vehicleList = await searchCustomerVehicles({ customerName: fullOrder.customerName });
      setVehicles(vehicleList);
    } catch (err: any) {
      message.error(err.response?.data?.message ?? 'Servis fişi yüklenirken hata oluştu');
      navigate('/service-orders');
    } finally {
      setLoading(false);
    }
  };

  const loadStatuses = async () => {
    try {
      const data = await getServiceOrderStatuses();
      setStatuses(data);
    } catch (error) {
      console.error('Statuses load error:', error);
    }
  };

  const loadServiceItems = async () => {
    try {
      const data = await fetchServiceItems(true);
      setServiceItems(data);
    } catch (error) {
      console.error('Service items load error:', error);
    }
  };

  const loadExternalServiceItems = async () => {
    try {
      const data = await fetchExternalServiceItems(true);
      setExternalServiceItems(data);
    } catch (error) {
      console.error('External service items load error:', error);
    }
  };

  const loadComplaintItems = async () => {
    try {
      const data = await fetchComplaintItems(true);
      setComplaintItems(data);
    } catch (error) {
      console.error('Complaint items load error:', error);
    }
  };

  const loadPersonnel = async () => {
    try {
      const data = await fetchPersonnelList();
      setUsers(data);
    } catch (error) {
      console.error('Personel listesi yüklenemedi:', error);
    }
  };

  const loadBrandsForAddVehicle = async () => {
    try {
      const data = await fetchBrands();
      setBrands(data);
    } catch (e) {
      console.error('Markalar yüklenemedi:', e);
    }
  };

  const openAddVehicleModal = () => {
    addVehicleForm.resetFields();
    addVehicleForm.setFieldsValue({ currentKm: 0 });
    setAddVehicleBrandId(undefined);
    setModels([]);
    setAddVehicleModalVisible(true);
  };

  const buildAddVehiclePayload = (reassign = false) => {
    const v = addVehicleForm.getFieldsValue();
    if (!selectedCustomer) throw new Error('Müşteri seçili değil');
    const rawPlate = (v.plate != null ? String(v.plate).trim() : '');
    const plate = rawPlate !== '' ? rawPlate.toLocaleUpperCase('tr-TR') : rawPlate;
    return {
      customerId: selectedCustomer.id,
      plate,
      vinChassis: v.vinChassis,
      brandId: v.brandId,
      modelId: v.modelId,
      make: v.make,
      model: v.model,
      year: v.year,
      color: v.color,
      engineNo: v.engineNo,
      currentKm: v.currentKm ?? 0,
      notes: v.notes,
      reassignToNewCustomer: reassign,
    };
  };

  const handleSaveNewVehicle = async () => {
    if (!selectedCustomer) {
      message.warning('Önce müşteri seçin');
      return;
    }
    try {
      await addVehicleForm.validateFields();
      setAddVehicleSaving(true);
      const created = await createCustomerVehicle(buildAddVehiclePayload(false));
      message.success('Araç eklendi');
      const vehicleList = await searchCustomerVehicles({ customerName: selectedCustomer.accountName });
      setVehicles(vehicleList);
      const toSelect = vehicleList.find((v) => v.id === created.id) ?? (created as CustomerVehicleDto);
      handleSelectVehicle(toSelect);
      setAddVehicleModalVisible(false);
      setVehicleModalVisible(false);
    } catch (err: any) {
      if (err.errorFields) {
        setAddVehicleSaving(false);
        return;
      }
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.code === VEHICLE_ASSIGNED_TO_OTHER_CODE) {
        const { plate, currentCustomerName, newCustomerName } = data;
        Modal.confirm({
          title: 'Araç atama onayı',
          content: `${plate} plakalı araç ${currentCustomerName} adına kayıtlıdır. ${newCustomerName} müşterisine atanacaktır. Devam edilsin mi?`,
          okText: 'Evet',
          cancelText: 'İptal',
          onOk: async () => {
            try {
              const created = await createCustomerVehicle(buildAddVehiclePayload(true));
              message.success('Araç yeni müşteriye atandı');
              const vehicleList = await searchCustomerVehicles({ customerName: selectedCustomer.accountName });
              setVehicles(vehicleList);
              const toSelect = vehicleList.find((v) => v.id === created.id) ?? (created as CustomerVehicleDto);
              handleSelectVehicle(toSelect);
              setAddVehicleModalVisible(false);
              setVehicleModalVisible(false);
            } catch (e2: any) {
              message.error(e2.response?.data?.message || 'Atama yapılamadı');
            } finally {
              setAddVehicleSaving(false);
            }
          },
          onCancel: () => setAddVehicleSaving(false),
        });
        setAddVehicleSaving(false);
        return;
      }
      message.error(data?.message || 'Araç eklenirken hata oluştu');
    } finally {
      setAddVehicleSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingOrder?.status === 6) {
        message.error('İptal edilmiş servis fişi güncellenemez.');
        return;
      }
      // Önce personel alanlarını kontrol et; boşsa notification göster (validateFields bu alanlarda reject edince catch'e düşüyor)
      const raw = form.getFieldsValue();
      if (!raw.responsibleUserId) {
        message.error('Fiş Sorumlu Personel seçiniz');
        return;
      }
      if (raw.status === 5 && !raw.deliveredByUserId) {
        message.error('Teslim Edildi seçildiğinde Teslim Eden Personel seçiniz');
        return;
      }

      const values = await form.validateFields();

      if (!selectedCustomer || !selectedVehicle) {
        message.error('Lütfen müşteri ve araç seçin');
        return;
      }

      const statusValue = values.status;

      if (lines.length === 0 && statusValue !== 1 && statusValue !== 6) {
        message.warning('En az bir satır eklemeniz gerekiyor');
        return;
      }

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.itemType === 1 && !line.serviceItemId) {
          message.warning(`${i + 1}. satır: Hizmet ekliyse hizmet seçilmelidir`);
          return;
        }
        if (line.itemType === 2 && !line.productId) {
          message.warning(`${i + 1}. satır: Parça ekliyse parça seçilmelidir`);
          return;
        }
        if (line.itemType === 3 && !line.externalServiceItemId) {
          message.warning(`${i + 1}. satır: Dış hizmet ekliyse dış hizmet seçilmelidir`);
          return;
        }
      }

      const orderData = {
        customerId: selectedCustomer.id,
        vehicleId: selectedVehicle.id,
        responsibleUserId: values.responsibleUserId,
        deliveredByUserId: values.deliveredByUserId,
        entryKm: values.entryKm,
        exitKm: values.exitKm,
        orderDate: values.orderDate.format('YYYY-MM-DDTHH:mm:ss'),
        status: values.status,
        notes: values.notes,
        complaintNotes: values.complaintNotes,
        complaintItemIds: values.complaintItemIds ?? [],
        useOrderPeriodicMaintenance: values.useOrderPeriodicMaintenance ?? false,
        orderPeriodicMaintenanceMonths: values.orderPeriodicMaintenanceMonths ?? 0,
        lines: lines.map((line, index) => ({
          lineNo: index + 1,
          itemType: line.itemType!,
          serviceItemId: line.serviceItemId,
          externalServiceItemId: line.externalServiceItemId,
          externalServiceAccountId: line.externalServiceAccountId,
          costPrice: line.costPrice,
          productId: line.productId,
          productVariantId: line.productVariantId,
          technicianUserId: line.technicianUserId,
          quantity: line.quantity || 1,
          unitPrice: line.unitPrice || 0,
          taxRate: line.taxRate ?? 20,
          notes: line.notes,
          periodicMaintenanceMonths: values.useOrderPeriodicMaintenance ? 0 : (line.periodicMaintenanceMonths ?? 0),
        })),
      };

      if (editingOrder) {
        await updateServiceOrder(editingOrder.id, {
          responsibleUserId: values.responsibleUserId,
          deliveredByUserId: values.deliveredByUserId,
          entryKm: values.entryKm,
          exitKm: values.exitKm,
          orderDate: values.orderDate.format('YYYY-MM-DDTHH:mm:ss'),
          status: values.status,
          notes: values.notes,
          complaintNotes: values.complaintNotes,
          complaintItemIds: values.complaintItemIds ?? [],
          useOrderPeriodicMaintenance: values.useOrderPeriodicMaintenance ?? false,
          orderPeriodicMaintenanceMonths: values.orderPeriodicMaintenanceMonths ?? 0,
          sendStatusSms,
        });
        const existingLineIds = (editingOrder.lines ?? [])
          .map((l: ServiceOrderLineDto) => l.id)
          .filter((lineId): lineId is number => lineId != null && lineId > 0);
        const currentLineIds = lines.filter((l) => l.id).map((l) => l.id!);
        const toDeleteIds = existingLineIds.filter((lineId) => !currentLineIds.includes(lineId));
        for (const lineId of toDeleteIds) {
          await deleteServiceOrderLine(lineId);
        }
        for (let index = 0; index < lines.length; index++) {
          const line = lines[index];
          const payload = {
            serviceItemId: line.serviceItemId,
            externalServiceItemId: line.externalServiceItemId,
            externalServiceAccountId: line.externalServiceAccountId,
            costPrice: line.costPrice,
            productId: line.productId,
            productVariantId:
              line.productVariantId != null && Number(line.productVariantId) > 0
                ? Number(line.productVariantId)
                : undefined,
            technicianUserId: line.technicianUserId,
            quantity: line.quantity ?? 1,
            unitPrice: line.unitPrice ?? 0,
            taxRate: line.taxRate ?? 20,
            notes: line.notes,
            periodicMaintenanceMonths: values.useOrderPeriodicMaintenance ? 0 : (line.periodicMaintenanceMonths ?? 0),
          };
          if (line.id) {
            await updateServiceOrderLine(line.id, payload);
          } else {
            const createPayload = () => ({
              orderId: editingOrder.id,
              lineNo: index + 1,
              itemType: line.itemType!,
              ...payload,
            });
            try {
              await createServiceOrderLine(createPayload());
            } catch (lineError: any) {
              const res = lineError.response?.data;
              if (res?.code === 'InsufficientStock') {
                await new Promise<void>((resolve, reject) => {
                  Modal.confirm({
                    title: 'Yetersiz stok',
                    content: `${res.productName ?? 'Ürün'} için stok yetersiz. Mevcut: ${res.currentStock ?? 0}, İstenen: ${res.requestedQuantity ?? 0}. Negatife düşürmek istiyor musunuz?`,
                    okText: 'Evet, düşür',
                    cancelText: 'İptal',
                    onOk: () =>
                      createServiceOrderLine({ ...createPayload(), allowNegativeStock: true })
                        .then(() => resolve())
                        .catch(reject),
                    onCancel: () => reject(new Error('Kullanıcı iptal etti')),
                  });
                });
              } else {
                throw lineError;
              }
            }
          }
        }
        message.success('Servis fişi güncellendi');
        const newStatus = values.status;
        if (newStatus === 5) {
          Modal.confirm({
            title: 'Tahsilata yönlendir',
            content: 'Tahsilata yönlendireyim mi?',
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: () => navigate(`/service-orders/${editingOrder.id}/collection`),
            onCancel: () => navigate('/service-orders'),
          });
        } else {
          navigate('/service-orders');
        }
      } else {
        await createServiceOrder(orderData);
        message.success('Servis fişi oluşturuldu');
        navigate('/service-orders');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedCustomer(null);
    setSelectedVehicle(null);
    setLines([]);
    setEditingOrder(null);
    navigate('/service-orders');
  };

  const handleAddLine = (type: number) => {
    const responsibleUserId = form.getFieldValue('responsibleUserId');
    const newLine: Partial<ServiceOrderLineDto> & { tempId: string } = {
      tempId: `temp-${Date.now()}`,
      itemType: type,
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
      taxRate: 20,
      technicianUserId: responsibleUserId ?? undefined,
      periodicMaintenanceMonths: 0,
    };
    setLines([...lines, newLine]);
  };

  const handleRemoveLine = (index: number) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const handleLineChange = (index: number, field: string, value: any) => {
    const updatedLines = [...lines];
    updatedLines[index] = { ...updatedLines[index], [field]: value };

    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : updatedLines[index].quantity || 1;
      const unitPrice = field === 'unitPrice' ? value : updatedLines[index].unitPrice || 0;
      updatedLines[index].totalPrice = quantity * unitPrice;
    }

    setLines(updatedLines);
  };

  const handleSelectServiceItem = (index: number, serviceItem: ServiceItemDto) => {
    const updatedLines = [...lines];
    const line = updatedLines[index];
    if (!line) return;
    const quantity = line.quantity ?? 1;
    updatedLines[index] = {
      ...line,
      itemType: 1,
      serviceItemId: serviceItem.id,
      serviceItemName: serviceItem.name,
      unitPrice: serviceItem.defaultPrice ?? 0,
      totalPrice: quantity * (serviceItem.defaultPrice ?? 0),
      periodicMaintenanceMonths: useOrderPeriodicMaintenance ? 0 : (serviceItem.intervalMonths ?? 0),
    };
    setLines(updatedLines);
  };

  const handleSelectExternalServiceItem = (index: number, externalItem: ExternalServiceItemDto) => {
    const updatedLines = [...lines];
    const line = updatedLines[index];
    if (!line) return;
    const quantity = line.quantity ?? 1;
    updatedLines[index] = {
      ...line,
      itemType: 3,
      externalServiceItemId: externalItem.id,
      externalServiceItemName: externalItem.name,
      unitPrice: externalItem.defaultPrice ?? 0,
      totalPrice: quantity * (externalItem.defaultPrice ?? 0),
    };
    setLines(updatedLines);
  };

  const handleOpenAddServiceItemModal = (lineIndex: number) => {
    setAddServiceItemLineIndex(lineIndex);
    addServiceItemForm.resetFields();
    addServiceItemForm.setFieldsValue({ defaultPrice: 0 });
    setAddServiceItemModalVisible(true);
  };

  const handleSaveNewServiceItem = async () => {
    try {
      const values = await addServiceItemForm.validateFields();
      setAddServiceItemSaving(true);
      const created = await createServiceItem({
        name: values.name,
        description: values.description,
        smsText: values.smsText,
        defaultPrice: values.defaultPrice ?? 0,
        intervalKm: values.intervalKm,
        intervalMonths: values.intervalMonths,
      });
      setServiceItems((prev) => [...prev, created]);
      if (addServiceItemLineIndex !== null) {
        handleSelectServiceItem(addServiceItemLineIndex, created);
      }
      message.success('Hizmet kalemi eklendi');
      setAddServiceItemModalVisible(false);
      setAddServiceItemLineIndex(null);
      addServiceItemForm.resetFields();
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Hizmet eklenirken hata oluştu');
    } finally {
      setAddServiceItemSaving(false);
    }
  };

  const handleOpenAddExternalServiceItemModal = (lineIndex: number) => {
    setAddExternalServiceItemLineIndex(lineIndex);
    addExternalServiceItemForm.resetFields();
    addExternalServiceItemForm.setFieldsValue({ defaultPrice: 0 });
    setAddExternalServiceItemModalVisible(true);
  };

  const handleSaveNewExternalServiceItem = async () => {
    try {
      const values = await addExternalServiceItemForm.validateFields();
      setAddExternalServiceItemSaving(true);
      const created = await createExternalServiceItem({
        name: values.name,
        description: values.description,
        defaultPrice: values.defaultPrice ?? 0,
      });
      setExternalServiceItems((prev) => [...prev, created]);
      if (addExternalServiceItemLineIndex !== null) {
        handleSelectExternalServiceItem(addExternalServiceItemLineIndex, created);
      }
      message.success('Dış hizmet kalemi eklendi');
      setAddExternalServiceItemModalVisible(false);
      setAddExternalServiceItemLineIndex(null);
      addExternalServiceItemForm.resetFields();
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Dış hizmet eklenirken hata oluştu');
    } finally {
      setAddExternalServiceItemSaving(false);
    }
  };

  const handleSelectExternalAccount = (account: Account) => {
    if (externalAccountLineIndex !== null) {
      handleLineChange(externalAccountLineIndex, 'externalServiceAccountId', account.id);
      handleLineChange(externalAccountLineIndex, 'externalServiceAccountName', account.accountName);
      setExternalAccountModalVisible(false);
      setExternalAccountLineIndex(null);
    }
  };

  const handleSelectProduct = (index: number, product: any, priceTypeId?: number) => {
    const price =
      priceTypeId === 2
        ? product.wholesalePrice ?? 0
        : priceTypeId === 3
          ? product.purchasePrice ?? 0
          : product.retailPrice ?? product.price ?? 0;
    const costPrice = product.purchasePrice ?? 0;
    const updatedLines = [...lines];
    const line = updatedLines[index];
    if (!line) return;
    const quantity = line.quantity ?? 1;
    updatedLines[index] = {
      ...line,
      productId: product.productId,
      productVariantId: product.variantId ?? null,
      productCode: product.productCode ?? '',
      productName: product.productName ?? '',
      unitPrice: price,
      totalPrice: quantity * price,
      costPrice,
    };
    setLines(updatedLines);
  };

  const handleSelectCustomer = (account: Account) => {
    setSelectedCustomer(account);
    setSelectedVehicle(null);
    setVehicles([]);
    form.setFieldsValue({ customerId: account.id });

    searchCustomerVehicles({ customerName: account.accountName }).then((data) => {
      setVehicles(data);
    });
  };

  const handleSelectVehicle = (vehicle: CustomerVehicleDto) => {
    setSelectedVehicle(vehicle);
    form.setFieldsValue({
      vehicleId: vehicle.id,
      entryKm: vehicle.currentKm,
    });
  };

  const getLineDescription = (line: Partial<ServiceOrderLineDto> & { tempId?: string }) => {
    if (line.itemType === 1) {
      return line.serviceItemName || serviceItems.find((s) => s.id === line.serviceItemId)?.name || '—';
    }
    if (line.itemType === 2) {
      const code = line.productCode || '';
      const name = line.productName || '';
      return [code, name].filter(Boolean).join(' - ') || '—';
    }
    if (line.itemType === 3) {
      return (
        line.externalServiceItemName ||
        externalServiceItems.find((e) => e.id === line.externalServiceItemId)?.name ||
        '—'
      );
    }
    return '—';
  };

  const getCustomerFullName = () => {
    if (!selectedCustomer) return '';
    const nameParts = [selectedCustomer.firstName, selectedCustomer.lastName].filter(Boolean).join(' ');
    return nameParts || selectedCustomer.accountName || '';
  };

  const getVehicleInfoText = () => {
    if (!selectedVehicle) return '';
    const brandModel = [selectedVehicle.brandName, selectedVehicle.modelName].filter(Boolean).join(' ');
    const makeModel = [selectedVehicle.make, selectedVehicle.model].filter(Boolean).join(' ');
    const fallbackInfo = (selectedVehicle as any).vehicleInfo as string | undefined;
    return brandModel || makeModel || fallbackInfo || '-';
  };

  const handlePrint = () => {
    const customerFullName = getCustomerFullName();
    const customerPhone = selectedCustomer?.phone || '';
    const companyName = selectedCustomer?.companyName || '';
    
    const plate = selectedVehicle?.plate ?? '';
    const vehicleInfo = getVehicleInfoText();
    const vinChassis = selectedVehicle?.vinChassis || '';
    const engineNo = selectedVehicle?.engineNo || '';
    const entryKm = form.getFieldValue('entryKm');
    
    const orderNo = editingOrder?.orderNo || '';
    const orderDateVal = form.getFieldValue('orderDate');
    const orderDate = orderDateVal && dayjs.isDayjs(orderDateVal) ? orderDateVal : dayjs();
    const dateStr = orderDate.format('DD.MM.YYYY');
    const timeStr = orderDate.format('HH:mm');
    
    const deliveredByUserId = form.getFieldValue('deliveredByUserId');
    const deliveredByUser = users.find(u => u.id === deliveredByUserId);
    const deliveredByUserName = deliveredByUser ? `${deliveredByUser.firstName} ${deliveredByUser.lastName}` : '';
    
    const complaintNotes = form.getFieldValue('complaintNotes') || '';
    const complaintItemIds = form.getFieldValue('complaintItemIds') || [];
    const selectedComplaintItems = complaintItems.filter(item => complaintItemIds.includes(item.id));
    const complaintText = selectedComplaintItems.length > 0 
      ? selectedComplaintItems.map(item => item.name).join(', ') + (complaintNotes ? ` - ${complaintNotes}` : '')
      : complaintNotes;
    
    const toplamHariç = lines.reduce((sum, l) => sum + (l.totalPrice ?? 0), 0);
    const toplamDahil = lines.reduce(
      (sum, l) => sum + (l.totalPrice ?? 0) * (1 + (l.taxRate ?? 20) / 100),
      0,
    );
    const kdvTutar = toplamDahil - toplamHariç;
    const fmt = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺';

    const rows = lines
      .map((line, i) => {
        const totalDahil = (line.totalPrice ?? 0) * (1 + (line.taxRate ?? 20) / 100);
        return `<tr>
        <td style="text-align:center;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${i + 1}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #e8e8e8;">${getLineDescription(line)}</td>
        <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${(line.quantity ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
        <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${(line.unitPrice ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
        <td style="text-align:center;padding:8px 6px;border-bottom:1px solid #e8e8e8;">%${line.taxRate ?? 20}</td>
        <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${fmt(line.totalPrice ?? 0)}</td>
        <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${fmt(totalDahil)}</td>
      </tr>`;
      })
      .join('');

    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Servis Fişi</title>
  <style>
    @page { 
      size: A4; 
      margin: 15mm;
    }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', system-ui, sans-serif; margin: 0; padding: 15mm; color: #1a1a1a; font-size: 12px; display: flex; flex-direction: column; min-height: 100vh; }
    .logo-area { 
      text-align: center;
      margin-bottom: 16px;
    }
    .logo-area img {
      width: 100%;
      max-width: 100%;
      height: auto;
      max-height: 40mm;
      object-fit: contain;
      display: block;
    }
    .page-break-logo {
      text-align: center;
      margin-bottom: 16px;
      page-break-before: always;
      break-before: page;
    }
    .page-break-logo img {
      width: 100%;
      max-width: 100%;
      height: auto;
      max-height: 40mm;
      object-fit: contain;
      display: block;
    }
    .content-wrapper { }
    .title { text-align: center; font-size: 24px; font-weight: bold; margin: 16px 0 20px 0; color: #1a1a1a; }
    .order-info { text-align: left; margin-bottom: 16px; font-size: 13px; }
    .info-row { display: flex; gap: 20px; margin-bottom: 16px; }
    .info-col { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .info-col h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    .info-item { margin-bottom: 6px; font-size: 12px; }
    .info-item strong { color: #495057; margin-right: 6px; }
    .complaint-section { margin: 16px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .complaint-section h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0 20px 0; }
    th { text-align: left; padding: 10px 6px; background: #2c3e50; color: #fff; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
    th:first-child { border-radius: 6px 0 0 0; }
    th:last-child { border-radius: 0 6px 0 0; }
    td { font-size: 12px; }
    .total-row { background: #f8f9fa; font-weight: 600; }
    .total-row td { padding: 12px 6px; border-top: 2px solid #2c3e50; font-size: 13px; }
    .signature-row { display: flex; gap: 20px; margin-top: 24px; page-break-inside: avoid; break-inside: avoid; }
    .signature-box { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .signature-box h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    .signature-box p { margin: 8px 0 0 0; font-size: 12px; }
    .warning-section { padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f5f5f5; }
    .warning-section h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #495057; }
    .warning-section p { margin: 0; font-size: 11px; color: #6c757d; line-height: 1.5; }
    .footer-section { padding-top: 24px; }
  </style>
</head>
<body>
  <div class="logo-area" style="text-align: center; margin-bottom: 16px;">
    <img src="${window.location.origin}/images/logo-print-full.png" alt="Logo" style="width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block;" onerror="this.style.display='none'">
  </div>
  <div class="content-wrapper">
  <div class="title">Servis Onarım Teklif Formu</div>
  <div class="order-info">
    <strong>Servis Fiş No:</strong> ${orderNo || '—'} - <strong>Tarih:</strong> ${dateStr} - <strong>Saat:</strong> ${timeStr}
  </div>
  <div class="info-row" style="display: flex; gap: 20px; margin-bottom: 16px;">
    <div class="info-col" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Müşteri Bilgileri</h4>
      <div class="info-item"><strong>Ad Soyad:</strong> ${customerFullName || '—'}</div>
      ${customerPhone ? `<div class="info-item"><strong>Telefon:</strong> ${customerPhone}</div>` : ''}
    </div>
    <div class="info-col" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Araç Bilgileri</h4>
      <div class="info-item"><strong>Plaka:</strong> ${plate || '—'}</div>
      ${vehicleInfo ? `<div class="info-item"><strong>Marka/Model:</strong> ${vehicleInfo}</div>` : ''}
      ${vinChassis ? `<div class="info-item"><strong>Şasi No:</strong> ${vinChassis}</div>` : ''}
      ${engineNo ? `<div class="info-item"><strong>Motor No:</strong> ${engineNo}</div>` : ''}
      ${entryKm ? `<div class="info-item"><strong>Km:</strong> ${entryKm.toLocaleString('tr-TR')}</div>` : ''}
    </div>
  </div>
  ${complaintText ? `
  <div class="complaint-section" style="margin: 16px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
    <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50;">Müşteri Şikayetleri</h4>
    <div>${complaintText}</div>
  </div>
  ` : ''}
  <table style="width: 100%; border-collapse: collapse; margin: 16px 0 20px 0;">
    <thead>
      <tr>
        <th style="width:36px;text-align:left;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-radius:6px 0 0 0;">#</th>
        <th style="text-align:left;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Kalem</th>
        <th style="width:72px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Adet</th>
        <th style="width:88px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Birim Fiyat</th>
        <th style="width:52px;text-align:center;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">KDV</th>
        <th style="width:100px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Toplam (KDV Hariç)</th>
        <th style="width:100px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-radius:0 6px 0 0;">Toplam (KDV Dahil)</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      <tr class="total-row" style="background:#f8f9fa;font-weight:600;">
        <td colspan="7" style="padding:12px 12px;border-top:2px solid #2c3e50;font-size:13px;text-align:right;">
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
            <div><strong>Toplam:</strong> ${fmt(toplamHariç)}</div>
            <div><strong>KDV:</strong> ${fmt(kdvTutar)}</div>
            <div><strong>Genel Toplam:</strong> ${fmt(toplamDahil)}</div>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <div class="signature-row" style="display: flex; gap: 20px; margin-top: 24px; page-break-inside: avoid; break-inside: avoid;">
    <div class="signature-box" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Teslim Eden</h4>
      <p style="margin: 8px 0 0 0; font-size: 12px;">${deliveredByUserName || '—'}</p>
    </div>
    <div class="signature-box" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Teslim Alan</h4>
      <p style="margin: 8px 0 0 0; font-size: 12px;">${customerFullName || '—'}${companyName ? `<br>${companyName}` : ''}</p>
    </div>
  </div>
  </div>
  <div class="footer-section" style="padding-top: 24px;">
    <div class="warning-section" style="padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f5f5f5;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #495057;">Uyarı</h4>
      <p style="margin: 0; font-size: 11px; color: #6c757d; line-height: 1.5;">Araç teslimi yalnızca mesai saatleri dâhilinde gerçekleştirilebilir. Bakım ve onarım süresi boyunca, gerekli görülmesi halinde, teşhis ve kalite kontrolü amacıyla test sürüşü yapılabilir.</p>
    </div>
  </div>
  <script>
    window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; };
  </script>
</body>
</html>`;

    // Yazdırma için içeriği işle - logo zaten HTML'de var, sadece sayfa kırılmalarına logo ekle
    const parser = new DOMParser();
    const doc = parser.parseFromString(printContent, 'text/html');
    const bodyElement = doc.body;
    
    if (bodyElement) {
      const contentWrapper = bodyElement.querySelector('.content-wrapper');
      if (contentWrapper) {
        // Tablo ve diğer büyük elementleri sayfa kırılma noktalarına göre böl
        const tables = contentWrapper.querySelectorAll('table');
        tables.forEach((table, index) => {
          if (index > 0) {
            // Her tablodan önce (ilk hariç) logo ekle
            const logoDiv = doc.createElement('div');
            logoDiv.className = 'page-break-logo';
            logoDiv.setAttribute('style', 'text-align: center; margin-bottom: 16px; page-break-before: always; break-before: page;');
            logoDiv.innerHTML = `<img src="${window.location.origin}/images/logo-print-full.png" style="width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block;" alt="Logo" onerror="this.style.display='none'">`;
            table.parentNode?.insertBefore(logoDiv, table);
          }
        });
      }
      
      const updatedContent = '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(updatedContent);
        win.document.close();
      }
    } else {
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(printContent);
        win.document.close();
      }
    }
  };

  // Liste sayfasından "Yazdır" ile gelindiyse (?print=1) fiş yüklendikten sonra yazdırmayı aç
  useEffect(() => {
    if (!orderId || !editingOrder || searchParams.get('print') !== '1') return;
    navigate(`/service-orders/${orderId}`, { replace: true });
    const t = setTimeout(() => handlePrint(), 200);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handlePrint deps'e eklenirse her render'da yazdırma tetiklenir
  }, [editingOrder, orderId, searchParams, navigate]);

  const handleSaveAsPDF = async () => {
    try {
      if (!selectedCustomer || !selectedVehicle) {
        message.error('Lütfen müşteri ve araç seçin');
        return;
      }

      // Print fonksiyonundaki mantığı TAMAMEN AYNI kullan
      const customerFullName = getCustomerFullName();
      const customerPhone = selectedCustomer?.phone || '';
      const companyName = selectedCustomer?.companyName || '';
      
      const plate = selectedVehicle?.plate ?? '';
      const vehicleInfo = getVehicleInfoText();
      const vinChassis = selectedVehicle?.vinChassis || '';
      const engineNo = selectedVehicle?.engineNo || '';
      const entryKm = form.getFieldValue('entryKm');
      
      const orderNo = editingOrder?.orderNo || '';
      const orderDateVal = form.getFieldValue('orderDate');
      const orderDate = orderDateVal && dayjs.isDayjs(orderDateVal) ? orderDateVal : dayjs();
      const dateStr = orderDate.format('DD.MM.YYYY');
      const timeStr = orderDate.format('HH:mm');
      
      const deliveredByUserId = form.getFieldValue('deliveredByUserId');
      const deliveredByUser = users.find(u => u.id === deliveredByUserId);
      const deliveredByUserName = deliveredByUser ? `${deliveredByUser.firstName} ${deliveredByUser.lastName}` : '';
      
      const complaintNotes = form.getFieldValue('complaintNotes') || '';
      const complaintItemIds = form.getFieldValue('complaintItemIds') || [];
      const selectedComplaintItems = complaintItems.filter(item => complaintItemIds.includes(item.id));
      const complaintText = selectedComplaintItems.length > 0 
        ? selectedComplaintItems.map(item => item.name).join(', ') + (complaintNotes ? ` - ${complaintNotes}` : '')
        : complaintNotes;
      
      const toplamHariç = lines.reduce((sum, l) => sum + (l.totalPrice ?? 0), 0);
      const toplamDahil = lines.reduce(
        (sum, l) => sum + (l.totalPrice ?? 0) * (1 + (l.taxRate ?? 20) / 100),
        0,
      );
      const kdvTutar = toplamDahil - toplamHariç;
      const fmt = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺';

      const rows = lines
        .map((line, i) => {
          const totalDahil = (line.totalPrice ?? 0) * (1 + (line.taxRate ?? 20) / 100);
          return `<tr>
        <td style="text-align:center;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${i + 1}</td>
        <td style="padding:8px 6px;border-bottom:1px solid #e8e8e8;">${getLineDescription(line)}</td>
        <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${(line.quantity ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
        <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${(line.unitPrice ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
        <td style="text-align:center;padding:8px 6px;border-bottom:1px solid #e8e8e8;">%${line.taxRate ?? 20}</td>
        <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${fmt(line.totalPrice ?? 0)}</td>
        <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${fmt(totalDahil)}</td>
      </tr>`;
        })
        .join('');

      // Print'teki HTML yapısını TAMAMEN AYNI kullan
      const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Servis Fişi</title>
  <style>
    @page { 
      size: A4; 
      margin: 15mm;
    }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', system-ui, sans-serif; margin: 0; padding: 15mm; color: #1a1a1a; font-size: 12px; display: flex; flex-direction: column; min-height: 100vh; }
    .logo-area { 
      text-align: center;
      margin-bottom: 16px;
    }
    .logo-area img {
      width: 100%;
      max-width: 100%;
      height: auto;
      max-height: 40mm;
      object-fit: contain;
      display: block;
    }
    .page-break-logo {
      text-align: center;
      margin-bottom: 16px;
      page-break-before: always;
      break-before: page;
    }
    .page-break-logo img {
      width: 100%;
      max-width: 100%;
      height: auto;
      max-height: 40mm;
      object-fit: contain;
      display: block;
    }
    .content-wrapper { }
    .title { text-align: center; font-size: 24px; font-weight: bold; margin: 16px 0 20px 0; color: #1a1a1a; }
    .order-info { text-align: left; margin-bottom: 16px; font-size: 13px; }
    .info-row { display: flex; gap: 20px; margin-bottom: 16px; }
    .info-col { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .info-col h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    .info-item { margin-bottom: 6px; font-size: 12px; }
    .info-item strong { color: #495057; margin-right: 6px; }
    .complaint-section { margin: 16px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .complaint-section h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0 20px 0; }
    th { text-align: left; padding: 10px 6px; background: #2c3e50; color: #fff; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
    th:first-child { border-radius: 6px 0 0 0; }
    th:last-child { border-radius: 0 6px 0 0; }
    td { font-size: 12px; }
    .total-row { background: #f8f9fa; font-weight: 600; }
    .total-row td { padding: 12px 6px; border-top: 2px solid #2c3e50; font-size: 13px; }
    .signature-row { display: flex; gap: 20px; margin-top: 24px; page-break-inside: avoid; break-inside: avoid; }
    .signature-box { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .signature-box h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    .signature-box p { margin: 8px 0 0 0; font-size: 12px; }
    .warning-section { padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f5f5f5; }
    .warning-section h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #495057; }
    .warning-section p { margin: 0; font-size: 11px; color: #6c757d; line-height: 1.5; }
    .footer-section { padding-top: 24px; }
  </style>
</head>
<body>
  <div class="logo-area" style="text-align: center; margin-bottom: 16px;">
    <img src="${window.location.origin}/images/logo-print-full.png" alt="Logo" style="width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block;" onerror="this.style.display='none'">
  </div>
  <div class="content-wrapper">
  <div class="title">Servis Onarım Teklif Formu</div>
  <div class="order-info">
    <strong>Servis Fiş No:</strong> ${orderNo || '—'} - <strong>Tarih:</strong> ${dateStr} - <strong>Saat:</strong> ${timeStr}
  </div>
  <div class="info-row" style="display: flex; gap: 20px; margin-bottom: 16px;">
    <div class="info-col" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Müşteri Bilgileri</h4>
      <div class="info-item"><strong>Ad Soyad:</strong> ${customerFullName || '—'}</div>
      ${customerPhone ? `<div class="info-item"><strong>Telefon:</strong> ${customerPhone}</div>` : ''}
    </div>
    <div class="info-col" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Araç Bilgileri</h4>
      <div class="info-item"><strong>Plaka:</strong> ${plate || '—'}</div>
      ${vehicleInfo ? `<div class="info-item"><strong>Marka/Model:</strong> ${vehicleInfo}</div>` : ''}
      ${vinChassis ? `<div class="info-item"><strong>Şasi No:</strong> ${vinChassis}</div>` : ''}
      ${engineNo ? `<div class="info-item"><strong>Motor No:</strong> ${engineNo}</div>` : ''}
      ${entryKm ? `<div class="info-item"><strong>Km:</strong> ${entryKm.toLocaleString('tr-TR')}</div>` : ''}
    </div>
  </div>
  ${complaintText ? `
  <div class="complaint-section" style="margin: 16px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
    <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50;">Müşteri Şikayetleri</h4>
    <div>${complaintText}</div>
  </div>
  ` : ''}
  <table style="width: 100%; border-collapse: collapse; margin: 16px 0 20px 0;">
    <thead>
      <tr>
        <th style="width:36px;text-align:left;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-radius:6px 0 0 0;">#</th>
        <th style="text-align:left;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Kalem</th>
        <th style="width:72px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Adet</th>
        <th style="width:88px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Birim Fiyat</th>
        <th style="width:52px;text-align:center;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">KDV</th>
        <th style="width:100px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Toplam (KDV Hariç)</th>
        <th style="width:100px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-radius:0 6px 0 0;">Toplam (KDV Dahil)</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      <tr class="total-row" style="background:#f8f9fa;font-weight:600;">
        <td colspan="7" style="padding:12px 12px;border-top:2px solid #2c3e50;font-size:13px;text-align:right;">
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
            <div><strong>Toplam:</strong> ${fmt(toplamHariç)}</div>
            <div><strong>KDV:</strong> ${fmt(kdvTutar)}</div>
            <div><strong>Genel Toplam:</strong> ${fmt(toplamDahil)}</div>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <div class="signature-row" style="display: flex; gap: 20px; margin-top: 24px; page-break-inside: avoid; break-inside: avoid;">
    <div class="signature-box" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Teslim Eden</h4>
      <p style="margin: 8px 0 0 0; font-size: 12px;">${deliveredByUserName || '—'}</p>
    </div>
    <div class="signature-box" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Teslim Alan</h4>
      <p style="margin: 8px 0 0 0; font-size: 12px;">${customerFullName || '—'}${companyName ? `<br>${companyName}` : ''}</p>
    </div>
  </div>
  </div>
  <div class="footer-section" style="padding-top: 24px;">
    <div class="warning-section" style="padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f5f5f5;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #495057;">Uyarı</h4>
      <p style="margin: 0; font-size: 11px; color: #6c757d; line-height: 1.5;">Araç teslimi yalnızca mesai saatleri dâhilinde gerçekleştirilebilir. Bakım ve onarım süresi boyunca, gerekli görülmesi halinde, teşhis ve kalite kontrolü amacıyla test sürüşü yapılabilir.</p>
    </div>
  </div>
</body>
</html>`;

      // Print ile TAMAMEN AYNI logo ekleme mantığı
      const parser = new DOMParser();
      const doc = parser.parseFromString(printContent, 'text/html');
      const bodyElement = doc.body;
      
      if (!bodyElement) {
        throw new Error('Body element not found');
      }
      
      // Print ile TAMAMEN AYNI logo ekleme mantığı
      const contentWrapper = bodyElement.querySelector('.content-wrapper');
      if (contentWrapper) {
        const tables = contentWrapper.querySelectorAll('table');
        tables.forEach((table, index) => {
          if (index > 0) {
            const logoDiv = doc.createElement('div');
            logoDiv.className = 'page-break-logo';
            logoDiv.setAttribute('style', 'text-align: center; margin-bottom: 16px; page-break-before: always; break-before: page;');
            logoDiv.innerHTML = `<img src="${window.location.origin}/images/logo-print-full.png" style="width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block;" alt="Logo" onerror="this.style.display='none'">`;
            table.parentNode?.insertBefore(logoDiv, table);
          }
        });
      }
      
      // html2pdf.js ile PDF'e çevir
      const html2pdf = (await import('html2pdf.js')).default;
      
      const opt = {
        margin: [15, 15, 15, 15] as [number, number, number, number],
        filename: `Servis_Fisi_${orderNo || 'Yeni'}_${dateStr.replace(/\./g, '_')}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm' as const, format: 'a4' as const, orientation: 'portrait' as const },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], before: '.page-break-logo', after: '.page-break-after', avoid: '.page-break-avoid' }
      };

      await html2pdf().set(opt).from(bodyElement).save();
      message.success('PDF başarıyla kaydedildi');
    } catch (err: any) {
      console.error('PDF kaydetme hatası:', err);
      message.error('PDF kaydedilirken hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    }
  };

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <div className="service-order-form-page">
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/service-orders')}>
            Geri
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {orderId ? 'Servis Fişini Düzenle' : 'Yeni Servis Fişi'}
          </Title>
        </Space>
        <Space wrap align="center">
          {orderId ? (
            <Space size={6} align="center">
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>SMS</span>
              <Switch checked={sendStatusSms} onChange={setSendStatusSms} />
            </Space>
          ) : null}
          <Button type="text" icon={<PrinterOutlined />} onClick={handlePrint}>
            Yazdır
          </Button>
          <Button type="text" icon={<FilePdfOutlined />} onClick={handleSaveAsPDF}>
            PDF Olarak Kaydet
          </Button>
          {editingOrder && [4, 5].includes(Number(form.getFieldValue('status'))) && (
            <Button onClick={() => navigate(`/service-orders/${editingOrder.id}/collection`)}>
              <DollarOutlined /> Tahsilat Yap
            </Button>
          )}
          <Button onClick={handleCancel}>İptal</Button>
          <Button type="primary" onClick={handleSave}>
            Kaydet
          </Button>
        </Space>
      </div>

      <Card>
        <Form form={form} layout="vertical">
          <Row gutter={16} align="top">
            <Col xs={24} md={12}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="status"
                    label="Durum"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    rules={[{ required: true }]}
                  >
                    <Select>
                      {statuses
                        .filter((s) => s.value !== 6)
                        .map((s) => (
                        <Select.Option key={s.value} value={s.value}>
                          {s.displayName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="orderDate"
                    label="Fiş Tarihi"
                    rules={[{ required: true, message: 'Lütfen tarih seçin' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" showTime />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Müşteri" required>
                    <Button
                      onClick={() => setAccountModalVisible(true)}
                      block
                      style={{ height: 66, whiteSpace: 'normal', textAlign: 'left', paddingTop: 6, paddingBottom: 6 }}
                    >
                      {selectedCustomer ? (
                        <div style={{ textAlign: 'left' }}>
                          <div>
                            <strong>Ad Soyad:</strong> {getCustomerFullName() || '-'}
                          </div>
                          {selectedCustomer.companyName && (
                            <div>
                              <strong>Firma Adı:</strong> {selectedCustomer.companyName}
                            </div>
                          )}
                          {selectedCustomer.phone && (
                            <div style={{ fontSize: 12, color: '#666' }}>
                              <strong>Cep:</strong> {selectedCustomer.phone}
                            </div>
                          )}
                        </div>
                      ) : (
                        'Müşteri Seç'
                      )}
                    </Button>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Araç" required>
                    <Button
                      onClick={() => setVehicleModalVisible(true)}
                      disabled={!selectedCustomer}
                      block
                      style={{ height: 66, whiteSpace: 'normal', textAlign: 'left', paddingTop: 6, paddingBottom: 6 }}
                    >
                      {selectedVehicle ? (
                        <div style={{ textAlign: 'left' }}>
                          <div>
                            <strong>Plaka:</strong> {selectedVehicle.plate}
                          </div>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            <strong>Marka/Model:</strong> {getVehicleInfoText()}
                          </div>
                        </div>
                      ) : (
                        'Araç Seç'
                      )}
                    </Button>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item name="entryKm" label="Giriş Km">
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item name="exitKm" label="Çıkış Km">
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="responsibleUserId"
                    label="Fiş Sorumlu Personel"
                    rules={[{ required: true, message: 'Fiş sorumlu personeli seçin' }]}
                  >
                    <Select placeholder="Seçin" allowClear>
                      {users.map((u) => (
                        <Select.Option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="deliveredByUserId"
                  label="Teslim Eden Personel"
                  rules={
                    watchedStatus === 5
                      ? [{ required: true, message: 'Teslim eden personeli seçin' }]
                      : []
                  }
                >
                  <Select
                    placeholder="Seçin"
                    allowClear
                    disabled={watchedStatus !== 5}
                  >
                    {users.map((u) => (
                      <Select.Option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              </Row>
            </Col>
            <Col xs={24} md={12}>
               <Form.Item name="complaintItemIds" label="Şikayet Kalemleri">
                <Select
                  mode="multiple"
                  placeholder="Şikayet kalemi seçin"
                  optionFilterProp="label"
                  showSearch
                  maxTagCount="responsive"
                >
                  {complaintItems.map((item) => (
                    <Select.Option key={item.id} value={item.id} label={item.name}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="complaintNotes" label="Genel Şikayet Metni">
                <TextArea rows={2} placeholder="Müşterinin genel şikayetini yazın" />
              </Form.Item>
               <Row gutter={16} align="middle">
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="useOrderPeriodicMaintenance"
                    valuePropName="checked"
                    label={
                      <Space size={4}>
                        <span>Hatırlatma Şekli</span>
                        <Tooltip title="Servis fişi bazında hatırlatma seçili olursa satırlardaki hatırlatmayı devre dışı bırakır ve fiş bazında belirlediğiniz bir süre sonra hatırlatma yapar.">
                          <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', cursor: 'help' }} />
                        </Tooltip>
                      </Space>
                    }
                  >
                    <Switch
                      checkedChildren="Fiş bazında"
                      unCheckedChildren="Satır bazında"
                      onChange={(checked: boolean) => {
                        if (checked) {
                          setLines((prev) =>
                            prev.map((line) => ({
                              ...line,
                              periodicMaintenanceMonths: 0,
                            })),
                          );
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  {useOrderPeriodicMaintenance && (
                    <Form.Item
                      name="orderPeriodicMaintenanceMonths"
                      label="Fiş Bazında Periyodik Bakım Süresi (Ay)"
                      rules={[{ required: true, message: 'Ay girin' }]}
                    >
                      <InputNumber min={0} step={1} style={{ width: '100%' }} />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>

          <Divider>Fiş Satırları</Divider>

          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Button type="dashed" icon={<PlusCircleOutlined />} onClick={() => handleAddLine(2)}>
                Parça Ekle
              </Button>
              <Button type="dashed" icon={<PlusCircleOutlined />} onClick={() => handleAddLine(1)}>
                Hizmet Ekle
              </Button>
              <Button type="dashed" icon={<PlusCircleOutlined />} onClick={() => handleAddLine(3)}>
                Dış Hizmet Ekle
              </Button>
            </Space>

            {lines.map((line, index) => (
              <Card key={line.tempId || line.id} size="small" style={{ marginBottom: 8, width: '100%' }}>
                <div style={{ overflowX: 'auto' }}>
                  <Row gutter={16} align="middle" style={{ width: '100%', minWidth: 880 }}>
                  <Col span={1}>
                    <strong>{index + 1}</strong>
                  </Col>
                  <Col span={3}>
                    <Select
                      value={line.itemType}
                      onChange={(value) => handleLineChange(index, 'itemType', value)}
                      style={{ width: '100%' }}
                    >
                      <Select.Option value={1}>HİZMET</Select.Option>
                      <Select.Option value={2}>PARÇA</Select.Option>
                      <Select.Option value={3}>DIŞ HİZMET</Select.Option>
                    </Select>
                  </Col>
                  <Col span={4}>
                    {line.itemType === 1 ? (
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          placeholder="Hizmet seç"
                          value={line.serviceItemId ?? undefined}
                          onChange={(value) => {
                            const item = serviceItems.find((si) => si.id === value);
                            if (item) handleSelectServiceItem(index, item);
                          }}
                          style={{ flex: 1, minWidth: 0 }}
                          optionLabelProp="label"
                          showSearch
                          optionFilterProp="label"
                        >
                          {[
                            ...serviceItems,
                            ...(line.serviceItemId && !serviceItems.some((si) => si.id === line.serviceItemId)
                              ? [{ id: line.serviceItemId, name: line.serviceItemName || 'Hizmet' }]
                              : []),
                          ].map((si) => (
                            <Select.Option key={si.id} value={si.id} label={si.name}>
                              {si.name}
                            </Select.Option>
                          ))}
                        </Select>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenAddServiceItemModal(index)} title="Yeni hizmet ekle" />
                      </Space.Compact>
                    ) : line.itemType === 3 ? (
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          placeholder="Dış hizmet seç"
                          value={line.externalServiceItemId}
                          onChange={(value) => {
                            const item = externalServiceItems.find((e) => e.id === value);
                            if (item) handleSelectExternalServiceItem(index, item);
                          }}
                          style={{ flex: 1, minWidth: 0 }}
                          optionLabelProp="label"
                        >
                          {externalServiceItems.map((e) => (
                            <Select.Option key={e.id} value={e.id} label={e.name}>
                              {e.name}
                            </Select.Option>
                          ))}
                        </Select>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenAddExternalServiceItemModal(index)} title="Yeni dış hizmet ekle" />
                      </Space.Compact>
                    ) : (
                      <Button
                        onClick={() => {
                          setProductModalVisible(true);
                          (window as any).__currentLineIndex = index;
                        }}
                        block
                      >
                        {line.productCode || line.productName
                          ? `${line.productCode || ''}${line.productCode && line.productName ? ' - ' : ''}${line.productName || ''}`
                          : 'Parça Seç'}
                      </Button>
                    )}
                  </Col>
                  <Col span={2}>
                    <InputNumber
                      value={line.quantity}
                      onChange={(value) => handleLineChange(index, 'quantity', value)}
                      min={0.01}
                      step={1}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={3}>
                    <InputNumber
                      value={line.unitPrice}
                      onChange={(value) => handleLineChange(index, 'unitPrice', value)}
                      min={0}
                      step={0.01}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={2}>
                    <strong style={{ fontSize: 11 }}>{(line.totalPrice || 0).toFixed(2)} ₺</strong>
                  </Col>
                  <Col span={2}>
                    <Select
                      className="service-order-kdv-select"
                      value={line.taxRate ?? 20}
                      onChange={(value) => handleLineChange(index, 'taxRate', value)}
                      style={{ width: '100%', fontSize: 12 }}
                      dropdownStyle={{ fontSize: 12 }}
                    >
                      {TAX_RATE_OPTIONS.map((r) => (
                        <Select.Option key={r} value={r} style={{ fontSize: 12 }}>
                          %{r}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={2}>
                    <strong style={{ fontSize: 11 }}>
                      {((line.totalPrice || 0) * (1 + (line.taxRate ?? 20) / 100)).toFixed(2)} ₺
                    </strong>
                  </Col>
                  <Col span={2}>
                    {line.itemType === 1 && !useOrderPeriodicMaintenance ? (
                      <InputNumber
                        value={line.periodicMaintenanceMonths}
                        onChange={(value) => handleLineChange(index, 'periodicMaintenanceMonths', value)}
                        min={0}
                        step={1}
                        style={{ width: '100%' }}
                        placeholder="Ay"
                      />
                    ) : (
                      <span>-</span>
                    )}
                  </Col>
                  <Col span={2}>
                    <Select
                      className="service-order-sorumlu-select"
                      placeholder="Sorumlu"
                      value={line.technicianUserId}
                      onChange={(value) => handleLineChange(index, 'technicianUserId', value)}
                      style={{ width: '100%' }}
                      allowClear
                      optionLabelProp="label"
                    >
                      {users.map((u) => (
                        <Select.Option key={u.id} value={u.id} label={`${u.firstName} ${u.lastName}`}>
                          {u.firstName} {u.lastName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={1}>
                    <Button type="link" danger icon={<MinusCircleOutlined />} onClick={() => handleRemoveLine(index)} />
                  </Col>
                </Row>
                </div>
                {line.itemType === 3 && (
                  <Row gutter={16} style={{ marginTop: 8, width: '100%' }}>
                    <Col xs={24} sm={8}>
                      <Button
                        onClick={() => {
                          setExternalAccountLineIndex(index);
                          setExternalAccountModalVisible(true);
                        }}
                        block
                      >
                        {line.externalServiceAccountName || 'Cari seç (yaptırılan yer)'}
                      </Button>
                    </Col>
                    <Col xs={24} sm={4}>
                      <InputNumber
                        placeholder="Maliyet fiyatı"
                        value={line.costPrice}
                        onChange={(value) => handleLineChange(index, 'costPrice', value)}
                        min={0}
                        step={0.01}
                        style={{ width: '100%' }}
                      />
                    </Col>
                    <Col xs={24} sm={6}>
                      <Input
                        placeholder="Açıklama"
                        value={line.notes}
                        onChange={(e) => handleLineChange(index, 'notes', e.target.value)}
                      />
                    </Col>
                  </Row>
                )}
              </Card>
            ))}
          </Space>

          <Divider />

          {(() => {
            const partLines = lines.filter((l) => l.itemType === 2);
            const serviceLines = lines.filter((l) => l.itemType === 1);
            const extLines = lines.filter((l) => l.itemType === 3);
            const lineKdvDahil = (net: number, rate: number) => net * (1 + (rate ?? 20) / 100);
            const malzemeKar = partLines.reduce(
              (s, l) => s + (l.totalPrice ?? 0) - (l.costPrice ?? 0) * (l.quantity ?? 1),
              0,
            );
            const malzemeKarDahil = partLines.reduce(
              (s, l) =>
                s +
                lineKdvDahil(l.totalPrice ?? 0, l.taxRate ?? 20) -
                (l.costPrice ?? 0) * (l.quantity ?? 1),
              0,
            );
            const iscilikKar = serviceLines.reduce((s, l) => s + (l.totalPrice ?? 0), 0);
            const iscilikKarDahil = serviceLines.reduce(
              (s, l) => s + lineKdvDahil(l.totalPrice ?? 0, l.taxRate ?? 20),
              0,
            );
            const disHizmetKar = extLines.reduce(
              (s, l) => s + (l.totalPrice ?? 0) - (l.costPrice ?? 0) * (l.quantity ?? 1),
              0,
            );
            const disHizmetKarDahil = extLines.reduce(
              (s, l) =>
                s +
                lineKdvDahil(l.totalPrice ?? 0, l.taxRate ?? 20) -
                (l.costPrice ?? 0) * (l.quantity ?? 1),
              0,
            );
            const toplamKar = malzemeKar + iscilikKar + disHizmetKar;
            const toplamKarDahil = malzemeKarDahil + iscilikKarDahil + disHizmetKarDahil;
            const toplamHariç = lines.reduce((sum, line) => sum + (line.totalPrice || 0), 0);
            const toplamDahil = lines.reduce(
              (sum, line) => sum + (line.totalPrice || 0) * (1 + (line.taxRate ?? 20) / 100),
              0,
            );
            const kdvTutar = toplamDahil - toplamHariç;
            const fmt = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺';
            return (
              <Row className="service-order-profit-summary-wrapper">
                <Col span={24}>
                  <div className="service-order-profit-summary" style={{ fontFamily: 'monospace' }}>
                    {isAdmin && (
                      <>
                        <div className="profit-summary-row">
                          <div />
                          <div>
                            <strong>KDV Hariç</strong>
                          </div>
                          <div>
                            <strong>KDV Dahil</strong>
                          </div>
                        </div>
                        <div className="profit-summary-row">
                          <div>Malzeme Satışı Kârı&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
                          <div>{fmt(malzemeKar)}</div>
                          <div>{fmt(malzemeKarDahil)}</div>
                        </div>
                        <div className="profit-summary-row">
                          <div>İşçilik (Hizmet) Kârı&nbsp;&nbsp;</div>
                          <div>{fmt(iscilikKar)}</div>
                          <div>{fmt(iscilikKarDahil)}</div>
                        </div>
                        <div className="profit-summary-row">
                          <div>Dış Hizmet Kârı&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
                          <div>{fmt(disHizmetKar)}</div>
                          <div>{fmt(disHizmetKarDahil)}</div>
                        </div>
                        <div className="profit-summary-row profit-summary-row-total">
                          <div>
                            <strong>Toplam Servis Kârı&nbsp;&nbsp;&nbsp;&nbsp;</strong>
                          </div>
                          <div>
                            <strong>{fmt(toplamKar)}</strong>
                          </div>
                          <div>
                            <strong>{fmt(toplamKarDahil)}</strong>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="profit-summary-row" style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                      <div><strong>Toplam:</strong> {fmt(toplamHariç)}</div>
                      <div><strong>KDV:</strong> {fmt(kdvTutar)}</div>
                      <div><strong>Genel Toplam:</strong> {fmt(toplamDahil)}</div>
                    </div>
                  </div>
                </Col>
              </Row>
            );
          })()}

          <Form.Item name="notes" label="Notlar">
            <TextArea rows={3} />
          </Form.Item>

          <DamageAssessmentSection
            vehicleId={selectedVehicle?.id ?? null}
            customerId={form.getFieldValue('customerId') ?? selectedCustomer?.id ?? null}
            serviceOrderId={editingOrder?.id ?? null}
            vehiclePlate={selectedVehicle?.plate}
            customerName={getCustomerFullName() || selectedCustomer?.companyName || selectedCustomer?.accountName || ''}
          />
        </Form>
      </Card>

      <AccountSelectModal
        visible={accountModalVisible}
        onClose={() => setAccountModalVisible(false)}
        onSelect={handleSelectCustomer}
      />

      <AccountSelectModal
        visible={externalAccountModalVisible}
        onClose={() => {
          setExternalAccountModalVisible(false);
          setExternalAccountLineIndex(null);
        }}
        onSelect={handleSelectExternalAccount}
      />

      <Modal title="Araç Seç" open={vehicleModalVisible} onCancel={() => setVehicleModalVisible(false)} footer={null} width={isMobile ? '100%' : 800} style={isMobile ? { maxWidth: '100%', top: 16, paddingBottom: 0 } : undefined}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button
            type="primary"
            onClick={openAddVehicleModal}
            disabled={!selectedCustomer}
          >
            Yeni Araç Ekle
          </Button>
        </div>
        <Table
          dataSource={vehicles}
          rowKey="id"
          scroll={isMobile ? { x: 400 } : undefined}
          columns={[
            { title: 'Plaka', dataIndex: 'plate', key: 'plate', width: 100 },
            {
              title: 'Marka/Model',
              key: 'info',
              width: 160,
              render: (_: any, v: CustomerVehicleDto) =>
                [v.brandName, v.modelName].filter(Boolean).join(' ').trim() ||
                [v.make, v.model].filter(Boolean).join(' ').trim() ||
                '-',
            },
            { title: 'Yıl', dataIndex: 'year', key: 'year', width: 70 },
            { title: 'Km', dataIndex: 'currentKm', key: 'currentKm', width: 90, render: (km) => km?.toLocaleString() },
          ]}
          onRow={(record) => ({
            onClick: () => {
              handleSelectVehicle(record);
              setVehicleModalVisible(false);
            },
            style: { cursor: 'pointer' },
          })}
        />
      </Modal>

      <Modal
        title="Yeni Araç Ekle"
        open={addVehicleModalVisible}
        onOk={handleSaveNewVehicle}
        onCancel={() => {
          setAddVehicleModalVisible(false);
          addVehicleForm.resetFields();
          setAddVehicleBrandId(undefined);
          setModels([]);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
        confirmLoading={addVehicleSaving}
      >
        <Form form={addVehicleForm} layout="vertical">
          <Form.Item
            name="plate"
            label="Plaka"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Lütfen plaka girin' }]}
          >
            <Input placeholder="Örn: 34ABC123" />
          </Form.Item>
          <Form.Item name="vinChassis" label="VIN/Şasi No">
            <Input placeholder="VIN veya şasi numarası" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="brandId" label="Marka">
                <Select
                  placeholder="Marka seçin (opsiyonel)"
                  showSearch
                  allowClear
                  onChange={(value) => {
                    setAddVehicleBrandId(value);
                    addVehicleForm.setFieldsValue({ modelId: undefined });
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={brands.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="modelId" label="Model">
                <Select
                  placeholder="Model seçin (opsiyonel)"
                  showSearch
                  allowClear
                  disabled={!addVehicleBrandId}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={models.map((m) => ({ value: m.id, label: m.name }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="make" label="Marka (Manuel)">
                <Input placeholder="Marka adı (marka seçilmediyse)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="model" label="Model (Manuel)">
                <Input placeholder="Model adı (model seçilmediyse)" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="year" label="Yıl">
                <InputNumber style={{ width: '100%' }} min={1900} max={new Date().getFullYear() + 1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="color" label="Renk">
                <Input placeholder="Renk" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currentKm" label="Mevcut Km">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="engineNo" label="Motor No">
            <Input placeholder="Motor numarası" />
          </Form.Item>
          <Form.Item name="notes" label="Notlar">
            <Input.TextArea rows={3} placeholder="Ek notlar" />
          </Form.Item>
        </Form>
      </Modal>

      <ProductSelectModal
        visible={productModalVisible}
        onClose={() => setProductModalVisible(false)}
        onSelect={(product, _selectedUnit, priceTypeId) => {
          const index = (window as any).__currentLineIndex;
          if (index !== undefined) {
            handleSelectProduct(index, product, priceTypeId);
            setProductModalVisible(false);
          }
        }}
        customerCategory="Retail"
        priceTypeId={1}
      />

      <Modal
        title="Yeni Hizmet Kalemi Ekle"
        open={addServiceItemModalVisible}
        onOk={handleSaveNewServiceItem}
        onCancel={() => {
          setAddServiceItemModalVisible(false);
          setAddServiceItemLineIndex(null);
          addServiceItemForm.resetFields();
        }}
        okText="Kaydet"
        cancelText="İptal"
        confirmLoading={addServiceItemSaving}
        width={isMobile ? '100%' : 520}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={addServiceItemForm} layout="vertical">
          <Form.Item name="name" label="Hizmet Adı" rules={[{ required: true, message: 'Lütfen hizmet adı girin' }]}>
            <Input placeholder="Örn: Motor Tamiri, Fren Bakımı" />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} placeholder="Hizmet açıklaması" />
          </Form.Item>
          <Form.Item name="smsText" label="SMS Metni">
            <Input placeholder="SMS metni" maxLength={500} />
          </Form.Item>
          <Form.Item name="defaultPrice" label="Varsayılan Fiyat" rules={[{ required: true, message: 'Lütfen varsayılan fiyat girin' }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} placeholder="0.00" />
          </Form.Item>
          <Form.Item name="intervalKm" label="Periyodik Bakım Aralığı (Km)">
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Örn: 10000" />
          </Form.Item>
          <Form.Item name="intervalMonths" label="Periyodik Bakım Aralığı (Ay)">
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Örn: 6" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Yeni Dış Hizmet Kalemi Ekle"
        open={addExternalServiceItemModalVisible}
        onOk={handleSaveNewExternalServiceItem}
        onCancel={() => {
          setAddExternalServiceItemModalVisible(false);
          setAddExternalServiceItemLineIndex(null);
          addExternalServiceItemForm.resetFields();
        }}
        okText="Kaydet"
        cancelText="İptal"
        confirmLoading={addExternalServiceItemSaving}
        width={isMobile ? '100%' : 520}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={addExternalServiceItemForm} layout="vertical">
          <Form.Item name="name" label="Hizmet Adı" rules={[{ required: true, message: 'Lütfen hizmet adı girin' }]}>
            <Input placeholder="Örn: Kaporta, Cam, Oto Elektrik" />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} placeholder="Hizmet açıklaması" />
          </Form.Item>
          <Form.Item name="defaultPrice" label="Varsayılan Fiyat" rules={[{ required: true, message: 'Lütfen varsayılan fiyat girin' }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} placeholder="0.00" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceOrderFormPage;
